import axios from 'axios'

export default {
  namespaced: true,
  state: {
    flags: [],
    loading: false,
    saving: false,
    error: null
  },
  mutations: {
    SET_FLAGS (state, flags) { state.flags = flags },
    SET_LOADING (state, val) { state.loading = val },
    SET_SAVING (state, val) { state.saving = val },
    SET_ERROR (state, err) { state.error = err },
    UPDATE_FLAG (state, updatedFlag) {
      const idx = state.flags.findIndex(
        f => f.featureIdentifier === updatedFlag.featureIdentifier
      )
      if (idx !== -1) {
        state.flags.splice(idx, 1, updatedFlag)
      }
    }
  },
  getters: {
    /**
     * Primary runtime check used by all UI components.
     * Returns the resolved (company-aware) enabled value — the value that came
     * back from GET /api/company/{id}/feature-flags which already has company
     * overrides applied server-side.
     */
    isEnabled: (state) => (featureIdentifier) => {
      const flag = state.flags.find(
        f => f.featureIdentifier === featureIdentifier
      )
      return flag ? flag.enabled : false
    },
    flagsByCategory: (state) => {
      const groups = {}
      state.flags.forEach(f => {
        const cat = f.category || 'General'
        if (!groups[cat]) groups[cat] = []
        groups[cat].push(f)
      })
      return groups
    },
    /**
     * Returns a Set of permissionKey strings whose flags are currently
     * disabled AND have hidePermissionWhenDisabled = true.
     * Used by PositionTypes.vue to conditionally render permission checkboxes.
     */
    hiddenPermissionKeys: (state) => {
      return new Set(
        state.flags
          .filter(f => !f.enabled && f.hidePermissionWhenDisabled && f.permissionKey)
          .map(f => f.permissionKey)
      )
    }
  },
  actions: {
    /**
     * Fetches flags resolved for the given company.
     * GET /api/company/{companyId}/feature-flags
     * The server applies any company-level overrides server-side, so the returned
     * flags always reflect the correct resolved values for that company.
     */
    async fetchFlags ({ commit, rootState }, { companyId } = {}) {
      const id = companyId || (rootState.userCurrentCompany && rootState.userCurrentCompany.companyId)
      if (!id) return []

      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const result = await axios.get(`/api/company/${id}/feature-flags`)
        commit('SET_FLAGS', result.data.featureFlags || [])
        return result.data.featureFlags || []
      } catch (e) {
        commit('SET_ERROR', e.response
          ? `${e.response.status}: ${e.response.statusText}`
          : e.message)
        return []
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Updates the global enabled value for a feature flag.
     * callerCompanyId = the RG Admin's own company (for auth).
     */
    async setGlobalEnabled ({ commit }, { callerCompanyId, featureIdentifier, enabled }) {
      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        const result = await axios.put(
          `/api/company/${callerCompanyId}/feature-flags/${encodeURIComponent(featureIdentifier)}`,
          { enabled }
        )
        commit('UPDATE_FLAG', result.data.featureFlag)
        return result.data.featureFlag
      } catch (e) {
        commit('SET_ERROR', e.response
          ? `${e.response.status}: ${e.response.statusText}`
          : e.message)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    },

    /**
     * Sets or replaces a company-level override for targetCompanyId.
     * callerCompanyId = the RG Admin's own company (for auth).
     * targetCompanyId = the company whose override is being set.
     */
    async setCompanyOverride ({ commit }, { callerCompanyId, targetCompanyId, featureIdentifier, enabled }) {
      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        const result = await axios.put(
          `/api/company/${callerCompanyId}/feature-flags/${encodeURIComponent(featureIdentifier)}/override/${targetCompanyId}`,
          { enabled }
        )
        return result.data.companyOverride
      } catch (e) {
        commit('SET_ERROR', e.response
          ? `${e.response.status}: ${e.response.statusText}`
          : e.message)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    },

    /**
     * Removes a company override, reverting targetCompanyId to the global value.
     * Re-fetches flags for callerCompanyId after deletion so the UI reflects the change.
     */
    async removeCompanyOverride ({ dispatch }, { callerCompanyId, targetCompanyId, featureIdentifier }) {
      await axios.delete(
        `/api/company/${callerCompanyId}/feature-flags/${encodeURIComponent(featureIdentifier)}/override/${targetCompanyId}`
      )
      // Re-fetch to get the reverted (global) value for the caller's company
      await dispatch('fetchFlags', { companyId: callerCompanyId })
    },

    /**
     * Sets ALL feature flag overrides for targetCompanyId to enabled/disabled in one call.
     * PUT /api/company/{callerCompanyId}/feature-flags/overrides/bulk/{targetCompanyId}
     * Re-fetches flags after the operation so the admin list reflects the new override counts.
     */
    async bulkSetCompanyOverrides ({ commit, dispatch }, { callerCompanyId, targetCompanyId, enabled }) {
      commit('SET_SAVING', true)
      commit('SET_ERROR', null)
      try {
        await axios.put(
          `/api/company/${callerCompanyId}/feature-flags/overrides/bulk/${targetCompanyId}`,
          { enabled }
        )
        await dispatch('fetchFlags', { companyId: callerCompanyId })
      } catch (e) {
        commit('SET_ERROR', e.response
          ? `${e.response.status}: ${e.response.statusText}`
          : e.message)
        throw e
      } finally {
        commit('SET_SAVING', false)
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/featureflags.js